import React from 'react';
import { nanoid } from 'nanoid';
import dodoStore from '../../shared/stores/dodo-store.js';
import {
  dispatchModal as dispatchModalSheet,
  hideModal as hideModalSheet,
} from '../../shared/stores/slices/modal-sheet.slice.js';
import { enqueueToast, hideToast } from '../../shared/stores/slices/toast.slice.js';
import { DodoFeedbackModal } from './modals/FeedbackModal.js';
/* eslint-disable class-methods-use-this */

/**
 * @typedef {import('../../shared/index.d.ts').CustomModalConfig} CustomModalConfig
 * @typedef {import('../../shared/index.d.ts').ModalSheetState} ModalSheetState
 * @typedef {import('../../shared/index.d.ts').ToastProps} ToastProps
 * @typedef {import('../../shared/index.d.ts').DodoUserAgentUiApi} DodoUserAgentUiApi
 */

/**
 *
 * @param {HTMLElement} elementRef
 * @param {string} eventName
 * @param {Function} recurrentCallback
 */
function dispatchDodoEvent(elementRef, eventName, recurrentCallback) {
  /**
   * @type {HTMLElement | undefined}
   */
  let element;

  try {
    element = elementRef?.closest('dodo-service-include');

    if (!element && recurrentCallback) {
      // @ts-ignore
      recurrentCallback(elementRef?.getRootNode()?.host);
      return;
    }

    element?.dispatchEvent(new CustomEvent(eventName));
  } catch (error) {
    console.error(error);
  }
}

/**
 * @implements {DodoUserAgentUiApi}
 */
export class DodoUI {
  #customModals = {};

  constructor() {
    this.registerModal('feedback', {
      Component: DodoFeedbackModal,
    });
  }

  /**
   *
   * @param {HTMLElement} anyMicroFrontendDOMNode
   */
  dispatchIdle(anyMicroFrontendDOMNode) {
    dispatchDodoEvent(anyMicroFrontendDOMNode, 'idle', this?.dispatchIdle);
  }

  /**
   *
   * @param {HTMLElement} anyMicroFrontendDOMNode
   */
  dispatchMaintenance(anyMicroFrontendDOMNode) {
    dispatchDodoEvent(anyMicroFrontendDOMNode, 'maintenance', this?.dispatchMaintenance);
  }

  /**
   *
   * @param {string} key
   * @param {CustomModalConfig} modal
   */
  registerModal(key, modal) {
    this.#customModals[key] = modal;
  }

  /**
   * Experimental, use with caution
   */
  async registerModalAsWebComponent(modalName, webComponentTag, webComponentImporter) {
    const moduleClass = await webComponentImporter();
    customElements.define(webComponentTag, moduleClass.default);

    this.registerModal(modalName, {
      Component: (props) => (
        <div className={`ebds-modal__content ebds-modal__content--${modalName}`}>
          {
            React.createElement(
              webComponentTag,
              Object.keys(props).reduce(
                (acc, key) => {
                  // eslint-disable-next-line no-param-reassign
                  acc[`attr-${key}`] = props[key];
                  return acc;
                },
                {},
              ),
            )
          }
        </div>
      ),
    });
  }

  /**
   * @returns {Record<string, CustomModalConfig>}
   */
  customModals() {
    return this.#customModals;
  }

  /**
   * @param {ModalSheetState} modal
   */
  dispatchModal(modal) {
    dodoStore.dispatch(dispatchModalSheet(modal));
  }

  dispatchHideModal() {
    dodoStore.dispatch(hideModalSheet(null));
  }

  /**
   *
   * @param {ToastProps} toast
   */
  dispatchToast(toast) {
    // eslint-disable-next-line no-param-reassign
    if (!toast.id) toast.id = nanoid();
    dodoStore.dispatch(enqueueToast(toast));
  }

  dispatchHideToast() {
    dodoStore.dispatch(hideToast());
  }
}
